import React, { CSSProperties, FC, ReactNode, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, DownloadIcon } from '@gamesb42/ui-kit';

import endpoints from 'consts/endpoints';
import { formatAmount, formatDate, getCurrency } from 'helpers/formatters';
import { getFileStorage } from 'components/forms/CompanyForm/reportsData';

import styles from './styles.module.scss';

import pdfSvg from 'assets/img/pdf.svg';
import Flex from 'components/Flex';
import DropZoneDocument from 'components/forms/CompanyForm/DropZoneDocument';
import UploadAOChangeStatus from 'components/modal/UploadAOChangeStatus';
const clipSvg = require('assets/img/clip.svg').default;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const getCurrencyWithAmount = (value, currency) =>
  value !== null && value !== undefined ? `${getCurrency(currency)}${formatAmount(Number(value))}` : '';

export const N_A_VALUE = 'N/A';

// Guards against technical values (null/undefined/NaN and their string forms) ever reaching the DOM.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isEmptyDisplayValue = (value: any): boolean => {
  if (value === null || value === undefined || value === '') return true;
  if (typeof value === 'number' && Number.isNaN(value)) return true;

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();

    if (normalized === 'null' || normalized === 'undefined' || normalized === 'nan') return true;
  }

  return false;
};

enum MAP_AO_TYPE_TO_STRING {
  classic = 'forms.activationOrder.classic',
  applaud_fix = 'forms.activationOrder.applaud',
  future_receivables = 'forms.activationOrder.futureReceivables',
  available_receivables = 'forms.activationOrder.availableReceivables',
}

export enum TypeEnum {
  TITLE,
  TEXT,
  FILE,
  CAPTION,
  HREF,
}

export type ColorT = 'green' | 'orange' | 'violet' | 'blue';
export type ValueFormatT = 'date' | 'money' | 'percent' | 'policy' | 'repaymentPlan';

export type PropsT = {
  type?: TypeEnum;
  valueFormatType?: ValueFormatT;
  color?: ColorT;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addComponent?: any;
  title?: string;
  currency?: string;
  additionalStyle?: CSSProperties;
  value?: string | number;
  content?: ReactNode;
  fileProps?: {
    isFileStile?: boolean;
    link?: string;
    name?: string;
  };
  widthLeft?: number;
  align?: 'left' | 'right' | 'inherit' | 'center' | 'justify';
  wrapStyle?: CSSProperties;
  titleStyle?: CSSProperties;
  rightContent?: ReactNode;
  setStatus?: any;
  hideColon?: boolean;
};

const Item: FC<PropsT> = ({
  type,
  title,
  color,
  value,
  fileProps,
  currency,
  valueFormatType = 'default',
  additionalStyle,
  widthLeft = 164,
  content,
  align = 'inherit',
  wrapStyle,
  titleStyle,
  rightContent,
  setStatus,
  hideColon,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [link, setLink] = useState(fileProps?.link);
  // @ts-ignore
  const { activationOrderId, purchaseRequestId } = useParams();

  const download = async (link: string) => {
    setIsLoading(true);
    await getFileStorage(endpoints.getStorageFileUrl(link));
    setIsLoading(false);
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const getFormattedValue: Record<ValueFormatT, (value: string) => string> = useMemo(
    () => ({
      date: (oldValue) => {
        if (isEmptyDisplayValue(oldValue)) return N_A_VALUE;

        return formatDate(oldValue) ?? N_A_VALUE;
      },
      money: (oldValue) => {
        if (isEmptyDisplayValue(oldValue)) return N_A_VALUE;

        return getCurrencyWithAmount(oldValue, currency) || N_A_VALUE;
      },
      percent: (oldValue) => {
        if (isEmptyDisplayValue(oldValue)) return N_A_VALUE;
        const numericValue = Number(oldValue);

        return Number.isNaN(numericValue) ? N_A_VALUE : `${formatAmount(numericValue)}%`;
      },
      policy: (oldValue) => (isEmptyDisplayValue(oldValue) ? N_A_VALUE : t(`selectFiledValues.${oldValue}`)),
      repaymentPlan: (oldValue) => (isEmptyDisplayValue(oldValue) ? N_A_VALUE : t(`selectFiledValues.${oldValue}`)),
      default: (oldValue) => (isEmptyDisplayValue(oldValue) ? N_A_VALUE : oldValue),
    }),
    [currency, t],
  );

  return (
    <div
      style={{
        gridTemplateColumns: type === TypeEnum.FILE ? '' : `${widthLeft}px ${title ? '1fr' : 'auto'}`,
        textAlign: align || 'inherit',
        ...wrapStyle,
      }}
      className={type === TypeEnum.FILE ? '' : styles.item}
    >
      {title && type !== TypeEnum.CAPTION && type !== TypeEnum.FILE && (
        <div style={titleStyle} className={styles.title}>{`${t(title)}${hideColon ? '' : ':'}`}</div>
      )}
      {content && content}
      {value !== undefined && type === TypeEnum.TITLE && (
        <div style={additionalStyle} className={`${styles.typeAo} ${styles[`${color}TypeAo`]}`}>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          {t(MAP_AO_TYPE_TO_STRING[value])}
        </div>
      )}
      {value !== undefined && type === TypeEnum.TEXT && (
        <div style={{ ...additionalStyle, wordBreak: 'break-word' }}>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          {getFormattedValue[valueFormatType](value)}
          {Boolean(rightContent) && rightContent}
        </div>
      )}
      {title && type === TypeEnum.CAPTION && <div className={styles.title}>{`${t(title)}`}</div>}
      {fileProps && type === TypeEnum.FILE && fileProps?.link && (
        <Flex justify="space-between" className={fileProps.isFileStile ? styles.contentFileNew : undefined}>
          <div
            className={styles.contentFileNewLeft}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              if (isLoading || !link) return;
              download(link);
            }}
          >
            <img src={pdfSvg} alt="pdfSvg" />
            <div style={{ width: '700px' }} className={styles.contentFileNewLeftText}>
              {fileProps.name}
            </div>
          </div>
          <Flex justify="flex-end" align="center" gap="20px">
            <Button
              type="iconBtn"
              icon={<DownloadIcon size={24} />}
              onClick={() => {
                if (isLoading || !link) return;
                download(link);
              }}
            />
          </Flex>
        </Flex>
      )}
      {fileProps && type === TypeEnum.HREF && fileProps?.link && (
        <span className={fileProps.isFileStile ? styles.contentFile : undefined}>
          {fileProps.isFileStile && <img src={clipSvg} alt="clip" />}
          <button
            style={additionalStyle}
            className={styles.buttonDownload}
            type="button"
            onClick={() => {
              if (isLoading || !fileProps.link) return;
              history.push(fileProps.link);
            }}
          >
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <div>{getFormattedValue[valueFormatType](value)}</div>
          </button>
        </span>
      )}
      <UploadAOChangeStatus
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        setStatus={setStatus}
        setLink={setLink}
        activationOrderId={activationOrderId ?? purchaseRequestId}
      />
    </div>
  );
};

export default Item;
